import { useRef, useState } from "react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE = "name,email,phone,note\nJane Doe,jane@example.com,+1 555 0100,Vegan option please\n";

export default function GuestImportDialog({ open, onOpenChange, activityId, onImported }) {
    const fileRef = useRef();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => { setResult(null); setBusy(false); };
    const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

    const downloadTemplate = () => {
        const blob = new Blob([TEMPLATE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "guest-list-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/activities/${activityId}/guests/import`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            onImported?.(data.activity);
            const summary = `${data.added} added${data.waitlisted ? ` · ${data.waitlisted} waitlisted` : ""}${data.skipped ? ` · ${data.skipped} skipped` : ""}`;
            toast.success(summary);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg rounded-none border-[#E5E5E3] p-0 shadow-none" data-testid="guest-import-dialog">
                <div className="p-8">
                    <DialogHeader className="text-left space-y-1">
                        <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">Import</p>
                        <DialogTitle className="font-heading text-2xl font-light tracking-tight text-[#111110]">Bulk-RSVP guests from CSV</DialogTitle>
                        <DialogDescription className="text-sm text-[#666664]">
                            Upload a CSV with columns: <code className="text-[#111110]">name, email, phone, note</code>. Duplicate emails are skipped. Activity capacity is respected (excess go on the waitlist).
                        </DialogDescription>
                    </DialogHeader>

                    {!result ? (
                        <div className="mt-6 space-y-4">
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={busy}
                                data-testid="guest-csv-dropzone"
                                className="w-full border border-dashed border-[#E5E5E3] hover:border-[#888885] transition-colors p-10 flex flex-col items-center justify-center gap-2 text-center"
                            >
                                <FileText className="w-6 h-6 text-[#666664]" strokeWidth={1.5} />
                                <span className="text-sm text-[#111110] font-medium">{busy ? "Uploading…" : "Choose CSV file"}</span>
                                <span className="text-xs text-[#888885]">or drag a file in once supported</span>
                            </button>
                            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                                onChange={(e) => upload(e.target.files?.[0])} data-testid="guest-csv-input" />
                            <button
                                type="button"
                                onClick={downloadTemplate}
                                data-testid="guest-csv-template-btn"
                                className="text-xs tracking-[0.15em] uppercase text-[#666664] hover:text-[#111110] inline-flex items-center gap-1.5"
                            >
                                <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Download CSV template
                            </button>
                        </div>
                    ) : (
                        <ResultBlock result={result} testId="guest-import-result" labels={{ added: "Added", waitlisted: "Waitlisted", skipped: "Skipped", total_rows: "Rows" }} />
                    )}
                </div>

                <DialogFooter className="border-t border-[#E5E5E3] px-8 py-5 bg-[#F9F9F8] flex-row gap-2 justify-end">
                    <Button variant="outline" onClick={() => handleClose(false)}
                        className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE]"
                        data-testid="guest-import-close-btn">
                        {result ? "Done" : "Cancel"}
                    </Button>
                    {!result && (
                        <Button onClick={() => fileRef.current?.click()} disabled={busy}
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                            data-testid="guest-import-pick-btn">
                            <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> Choose file
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function ResultBlock({ result, labels, testId }) {
    return (
        <div className="mt-6 space-y-4" data-testid={testId}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(labels).map(([key, label]) => (
                    <div key={key} className="border border-[#E5E5E3] p-4">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">{label}</div>
                        <div className="font-heading text-3xl font-light text-[#111110] tabular-nums mt-1" data-testid={`${testId}-${key}`}>
                            {result[key] ?? 0}
                        </div>
                    </div>
                ))}
            </div>
            {result.errors?.length > 0 && (
                <div className="border border-[#E5E5E3] p-4">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2 inline-flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> Issues
                    </div>
                    <ul className="text-xs text-[#666664] space-y-1 max-h-32 overflow-y-auto">
                        {result.errors.map((e, i) => <li key={i} className="font-mono">{e}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}
